// WCAG 2.x contrast maths for the colour check (contrast_check_controller.js, accessibility_check_controller.js).

export const BLACK = "#000000"
export const WHITE = "#ffffff"

// "#abc" / "#aabbcc" (with or without "#") as "#aabbcc", the only form a colour swatch accepts; null
// otherwise. Stored colours may be three-digit (CssHexColorValidator allows it).
export function fullHex(color) {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color || "")
  if (!match) return null

  const digits = match[1].length === 3 ? [...match[1]].map((digit) => digit + digit).join("") : match[1]
  return `#${digits.toLowerCase()}`
}

// Relative luminance, or null for anything that isn't a hex colour.
export function luminance(color) {
  const channels = rgb(color)
  if (!channels) return null

  const [r, g, b] = channels.map((value) => {
    const channel = value / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// The contrast ratio of two colours (1 to 21), or null if either isn't a hex colour.
export function contrastRatio(first, second) {
  const a = luminance(first)
  const b = luminance(second)
  if (a === null || b === null) return null

  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

// One decimal, rounded down, so a pair just under the minimum never reads as meeting it (4.47 -> "4.4:1").
export function formatRatio(ratio) {
  return `${Math.floor(ratio * 10) / 10}:1`
}

// The lowest contrast of `foreground` against any of `backgrounds` (a colour drawn on several surfaces must
// read on each), or null if any colour isn't hex or there are no backgrounds.
export function minContrast(foreground, backgrounds) {
  const ratios = [backgrounds].flat().map((background) => contrastRatio(foreground, background))
  return ratios.length > 0 && ratios.every((ratio) => ratio !== null) ? Math.min(...ratios) : null
}

// The closest shade of `foreground` (only lightness changes; a near-grey becomes pure grey) that reaches `min` on
// every background; if none does, black or white, whichever reads better (it can still fall short). Null if a
// colour isn't hex or there are no backgrounds.
export function nearestPassing(foreground, backgrounds, min) {
  const all = [backgrounds].flat()
  const hsl = toHsl(foreground)
  if (!hsl || minContrast(BLACK, all) === null) return null

  const blackOrWhite = minContrast(BLACK, all) >= minContrast(WHITE, all) ? BLACK : WHITE
  const [h, s, l] = hsl
  const grey = s < 0.1
  if (grey && (l >= 0.99 || l <= 0.01)) return blackOrWhite
  for (let step = 1; step <= 100; step++) {
    for (const next of [l - step / 100, l + step / 100]) {
      if (next < 0 || next > 1) continue
      const hex = grey ? fromHsl(0, 0, next) : fromHsl(h, s, next)
      if (minContrast(hex, all) >= min) return hex
    }
  }
  return blackOrWhite
}

function rgb(color) {
  const hex = fullHex(color)
  return hex ? [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16)) : null
}

function toHsl(color) {
  const channels = rgb(color)
  if (!channels) return null

  const [r, g, b] = channels.map((value) => value / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  return [h / 6, s, l]
}

function fromHsl(h, s, l) {
  const hue = (p, q, t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const channels = s === 0 ? [l, l, l] : [hue(p, q, h + 1 / 3), hue(p, q, h), hue(p, q, h - 1 / 3)]
  return "#" + channels.map((c) => Math.round(c * 255).toString(16).padStart(2, "0")).join("")
}
